import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { syncGuestCartToBackend } from "../../store/slices/cartSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { loginUser, clearError, initiateGoogleLogin } from "../../store/slices/authSlice";
import { useToast } from "../../components/Toast";

interface LoginData {
  email: string;
  phoneNumber: string;
  password: string;
}

const Login = () => {
  // const [formData, setFormData] = useState<LoginData>({
  //   email: "",
  //   phoneNumber: "",
  //   password: "",
  // });
  const [showPassword, setShowPassword] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const dispatch = useAppDispatch();
  const { isLoading, error, isAuthenticated } = useAppSelector(
    (state) => state.auth
  );

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    const sessionExpiredMessage = sessionStorage.getItem("sessionExpiredMessage");
    if (sessionExpiredMessage) {
      sessionStorage.removeItem("sessionExpiredMessage");
      toast(sessionExpiredMessage, "warning");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) {
      toast(error, "error");
      dispatch(clearError());
    }
  }, [error, toast, dispatch]);

  useEffect(() => {
    if (isAuthenticated && !isSyncing) {
      // Don't navigate yet if we're still syncing cart
      navigate("/home");
    }
  }, [isAuthenticated, isSyncing, navigate]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // input values
    const formData = new FormData(event.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    handleLogin(email, password);
  };

  const handleLogin = async (emailOrPhone: string, password: string) => {
    try {
      setIsSyncing(true);
      
      // Dispatch login and wait for it to complete
      await dispatch(loginUser({ email: emailOrPhone, password })).unwrap();
      await dispatch(syncGuestCartToBackend()).unwrap();
      
      // Check if user came from checkout
      const shouldRedirectToCheckout = sessionStorage.getItem('checkout_redirect');
      
      if (shouldRedirectToCheckout) {
        sessionStorage.removeItem('checkout_redirect');
        navigate('/checkout');
      } else {
        navigate('/home');
      }
    } finally {
      setIsSyncing(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setIsSyncing(true);
      await dispatch(initiateGoogleLogin()).unwrap();
      
      // Sync cart after Google login
      await dispatch(syncGuestCartToBackend()).unwrap();
      
      const shouldRedirectToCheckout = sessionStorage.getItem('checkout_redirect');
      if (shouldRedirectToCheckout) {
        sessionStorage.removeItem('checkout_redirect');
        navigate('/checkout');
      }
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div>
      <section className="sm:flex sm:justify-center sm:items-center sm:min-h-screen">
        <div className="p-5 sm:p-14 sm:border sm:w-[606px] sm:flex sm:flex-col sm:justify-center sm:rounded-2xl">
          <h1 className="text-2xl font-bold text-customBrown leading-7 pb-4">
            Welcome back, login to your account
          </h1>
          <p className="text-base text-shadeGray">
            Login with your email address or phone number and password
          </p>
          <form onSubmit={handleSubmit} className="space-y-4 pt-4">
            <div>
              <label
                htmlFor="email"
                className="leading-8 text-sm text-customBrown font-normal"
              >
                Email Address or Phone number
              </label>
              <input
                id="email"
                name="email"
                type="text"
                placeholder="Enter your email address or phone number"
                className="w-full p-4 border rounded-lg text-base shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div className="relative">
              <label
                htmlFor="password"
                className="leading-8 text-sm text-customBrown font-normal"
              >
                Password
              </label>
              <input
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="**************"
                className="w-full p-4 border rounded-lg text-base shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="absolute flex right-4 top-[60px] transform -translate-y-1/2 text-sm text-primary"
                type="button"
              >
                {showPassword ? "Hide" : "Show"}
              </button>
              <Link to="/recover-password">
                <p className="text-right cursor-pointer py-2  text-sm text-shadeGray font-normal">
                  Forgot Password?
                </p>
              </Link>
            </div>
            <button
              type="submit"
              className="bg-primary rounded-lg p-4 w-full text-base text-white disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading || isSyncing}
            >
              {isLoading ? "Logging in..." : isSyncing ? "Syncing cart..." : "Login"}
            </button>
          </form>
          <p className="text-center text-sm text-black py-4">
            Don't have an account?
            <a href="/account-type" className="text-primary hover:underline ml-1 font-bold">
              Create
            </a>
          </p>
          <div className="flex items-center gap-4 pb-3">
            <div className="flex-1 border-t border-gray-300"></div>
            <span className="text-customBrown text-sm">OR</span>
            <div className="flex-1 border-t border-gray-300"></div>
          </div>
          <div className="flex flex-col gap-4 lg:flex-row">
            <button
              onClick={handleGoogleLogin}
              disabled={isLoading || isSyncing}
              type="button"
              className="flex items-center justify-center gap-3 border border-[#6558F5] rounded-xl py-5 lg:py-4 w-full hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <img src="/google.svg" alt="Google" />
              <p>{isSyncing ? "Syncing cart..." : "Continue with Google"}</p>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Login;
